/**
 * Premier Auto Designs - Forms Analytics & Incomplete Lead Recovery Engine
 * Automatically tracks form views and captures partial/abandoned leads before exit.
 */
(function () {
  'use strict';

  const API_BASE = 'https://premierautosform.vercel.app';
  const SHOP_DOMAIN = (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : window.location.hostname;

  // 1. Session & Interaction Identifier
  function getSessionId() {
    let sid = sessionStorage.getItem('_pad_form_session_id');
    if (!sid) {
      sid = 'pad_sess_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
      sessionStorage.setItem('_pad_form_session_id', sid);
    }
    return sid;
  }

  const sessionId = getSessionId();

  // 2. Helper: Extract all current form values
  function extractFormData(container) {
    const data = {};
    const elements = container.querySelectorAll('input, select, textarea');

    elements.forEach(function (el) {
      if (!el.name || el.type === 'submit' || el.type === 'button' || el.type === 'password' || el.type === 'hidden' && el.name.startsWith('_')) {
        return;
      }

      if (el.type === 'checkbox') {
        if (el.checked) {
          data[el.name] = data[el.name] ? data[el.name] + ', ' + el.value : el.value;
        }
      } else if (el.type === 'radio') {
        if (el.checked) {
          data[el.name] = el.value;
        }
      } else if (el.value && el.value.trim() !== '') {
        data[el.name] = el.value.trim();
      }
    });

    return data;
  }

  // 3. Helper: Extract email, phone, name from data
  function extractContactChannels(data) {
    let email = '';
    let phone = '';
    let name = '';

    for (let key in data) {
      const lowerKey = key.toLowerCase();
      const val = String(data[key]).trim();

      if (!email && (lowerKey.includes('email') || val.includes('@'))) {
        if (val.includes('@') && val.length > 4) {
          email = val;
        }
      }

      if (!phone && (lowerKey.includes('phone') || lowerKey.includes('tel') || lowerKey.includes('mobile'))) {
        const digits = val.replace(/[^0-9]/g, '');
        if (digits.length >= 7) {
          phone = val;
        }
      }

      if (!name && (lowerKey.includes('name') || lowerKey.includes('full_name') || lowerKey === 'fullname')) {
        name = val;
      }
    }

    return { email: email, phone: phone, name: name };
  }

  // 4. Helper: Determine Form Name
  function resolveFormName(formEl, container) {
    if (container && container.dataset && container.dataset.formName) {
      return container.dataset.formName.trim();
    }
    if (formEl && formEl.dataset && formEl.dataset.formName) {
      return formEl.dataset.formName.trim();
    }
    const formNameInput = (formEl || container).querySelector('input[name="form_name"], input[name="formName"]');
    if (formNameInput && formNameInput.value) {
      return formNameInput.value.trim();
    }
    const titleEl = (container || formEl).querySelector('.form-title, .modal-title, h1, h2, h3');
    if (titleEl && titleEl.textContent && titleEl.textContent.trim()) {
      const text = titleEl.textContent.trim();
      if (text.length < 60) return text;
    }
    return document.title.split('|')[0].trim() || 'General Inquiry Form';
  }

  // 5. Track Form View (Once per visitor session per form)
  function trackFormView(formName) {
    if (!formName) return;
    const viewKey = '_pad_view_tracked_' + formName;
    if (sessionStorage.getItem(viewKey)) {
      return; // Already counted in this session
    }

    const payload = {
      shop: SHOP_DOMAIN,
      form_name: formName
    };

    fetch(API_BASE + '/api/forms/track', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true
    }).then(function () {
      sessionStorage.setItem(viewKey, '1');
    }).catch(function (err) {
      console.warn('[PAD Forms] View tracking skipped:', err);
    });
  }

  // 6. Capture Incomplete / Abandoned Form Data
  const debounceTimers = {};

  function sendIncompleteLead(formName, data, status, isBeacon) {
    if (!formName) return;

    // Do not capture if this form was successfully submitted
    if (sessionStorage.getItem('_pad_submitted_' + formName)) {
      return;
    }

    const contact = extractContactChannels(data);
    const hasEmail = contact.email && contact.email.includes('@');
    const hasPhone = contact.phone && contact.phone.replace(/[^0-9]/g, '').length >= 7;

    // Require at least Email or Phone
    if (!hasEmail && !hasPhone) {
      return;
    }

    const payload = {
      shop: SHOP_DOMAIN,
      session_id: sessionId,
      form_name: formName,
      page_url: window.location.href,
      page_title: document.title,
      email: contact.email || null,
      phone: contact.phone || null,
      name: contact.name || null,
      form_data: data,
      status: status || 'incomplete'
    };

    const payloadStr = JSON.stringify(payload);

    if (isBeacon && navigator.sendBeacon) {
      const blob = new Blob([payloadStr], { type: 'application/json' });
      navigator.sendBeacon(API_BASE + '/api/forms/incomplete', blob);
    } else {
      fetch(API_BASE + '/api/forms/incomplete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payloadStr,
        keepalive: true
      }).catch(function (e) {
        console.warn('[PAD Forms] Incomplete capture:', e);
      });
    }
  }

  function queueIncompleteLead(formName, container, status) {
    if (debounceTimers[formName]) {
      clearTimeout(debounceTimers[formName]);
    }

    debounceTimers[formName] = setTimeout(function () {
      const data = extractFormData(container);
      sendIncompleteLead(formName, data, status || 'incomplete', false);
    }, 1200);
  }

  // 7. Initialize Form Tracking & Observers
  function initFormTracking() {
    const containers = document.querySelectorAll(
      '[data-form-name], form, .contact-form, .hero-multistep-form-wrapper, .hero-form-modal, .hero-popup-form'
    );

    const trackedNames = new Set();

    containers.forEach(function (container) {
      const formEl = container.tagName === 'FORM' ? container : container.querySelector('form');
      const targetContainer = formEl || container;
      const formName = resolveFormName(formEl, container);

      if (!formName || trackedNames.has(formName)) {
        return;
      }
      trackedNames.add(formName);

      // Track View
      trackFormView(formName);

      // Listen for field inputs & blurs
      targetContainer.addEventListener('input', function (e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
          queueIncompleteLead(formName, targetContainer, 'incomplete');
        }
      });

      targetContainer.addEventListener('blur', function (e) {
        if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA')) {
          const data = extractFormData(targetContainer);
          sendIncompleteLead(formName, data, 'incomplete', false);
        }
      }, true);

      // Listen for multi-step transitions (e.g. clicking Next)
      const nextButtons = targetContainer.querySelectorAll('.ms-next-btn, .next-step-btn, button[class*="next"], [data-action="next"]');
      nextButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          setTimeout(function () {
            const data = extractFormData(targetContainer);
            sendIncompleteLead(formName, data, 'incomplete', false);
          }, 100);
        });
      });

      // Hook form submit success / failure
      if (formEl) {
        formEl.addEventListener('submit', function () {
          const data = extractFormData(targetContainer);
          data.session_id = sessionId;
        });
      }
    });

    // 8. Page Exit / Tab Switch Abandonment Trigger (Visibility Change & BeforeUnload)
    const handleExit = function () {
      trackedNames.forEach(function (formName) {
        if (!sessionStorage.getItem('_pad_submitted_' + formName)) {
          const matching = document.querySelector('[data-form-name="' + formName + '"]') || document.querySelector('form');
          if (matching) {
            const data = extractFormData(matching);
            sendIncompleteLead(formName, data, 'abandoned', true);
          }
        }
      });
    };

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'hidden') {
        handleExit();
      }
    });

    window.addEventListener('beforeunload', handleExit);
  }

  // Global helper for sections submitting via JS fetch
  window.PAD_FORMS = {
    getSessionId: getSessionId,
    trackView: trackFormView,
    captureIncomplete: function (formName, data, status) {
      sendIncompleteLead(formName, data, status, false);
    },
    markCompleted: function (formName) {
      if (formName) {
        sessionStorage.setItem('_pad_submitted_' + formName, '1');
      }
    },
    markFailed: function (formName, data, errorMsg) {
      if (formName) {
        const payload = Object.assign({}, data, { error_message: errorMsg });
        sendIncompleteLead(formName, payload, 'failed', false);
      }
    }
  };

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initFormTracking);
  } else {
    initFormTracking();
  }
})();
